import { type QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { myChatsKeys } from '@/features/messages/hooks/useMyChats'
import { playIntentKeys } from '@/features/play-intents/hooks/usePlayIntents'
import { chatKeys } from '@/features/sessions/hooks/useChatMessages'

import { connectChatStream, FatalAuthError } from '../api/chatStreamClient'
import {
  type ChatMessage,
  type ChatStreamEvent,
  type ChatStreamState,
  groupChatKey,
} from '../types'

interface UseChatStreamOpts {
  jwt: string | null
  apiBase: string
}

const MAX_RECONNECTS_BEFORE_FALLBACK = 3
const FALLBACK_RETRY_INTERVAL_MS = 5 * 60_000

/**
 * Singleton hook — monta UNA conexión SSE al endpoint /me/chat-stream
 * y aplica los eventos entrantes a las caches de TanStack Query:
 *   - chatKeys.messages(sessionId): append filtrado por id (idempotente)
 *   - myChatsKeys.list: invalidate para refrescar badges unread
 *
 * Devuelve el estado actual del stream — los hooks consumers
 * (useChatMessages, useMyChats) lo leen para reactivar polling en fallback.
 */
export function useChatStream(opts: UseChatStreamOpts): ChatStreamState {
  const qc = useQueryClient()
  const [state, setState] = useState<ChatStreamState>('CLOSED')
  const reconnectCountRef = useRef(0)
  const lastEventIdRef = useRef<string | undefined>(undefined)
  const abortRef = useRef<AbortController | null>(null)
  const fallbackRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!opts.jwt) {
      setState('CLOSED')
      return
    }

    let cancelled = false

    const connect = () => {
      if (cancelled) return
      setState('CONNECTING')
      const abort = new AbortController()
      abortRef.current = abort

      connectChatStream({
        apiBase: opts.apiBase,
        jwt: opts.jwt!,
        lastEventId: lastEventIdRef.current,
        signal: abort.signal,
        // Abrir no significa que funcione: con Redis caído el servidor responde 200 y el
        // stream muere al momento. Si aquí se diera por bueno (contador a 0, OPEN), nunca se
        // llegaría a POLLING_FALLBACK y el chat se quedaba congelado hasta recargar. Se da
        // por vivo cuando llega algo (evento o latido, el primero a los 2 s): `onAlive`.
        onOpen: () => {},
        onAlive: () => {
          reconnectCountRef.current = 0
          setState('OPEN')
        },
        onEvent: (event: ChatStreamEvent) => {
          lastEventIdRef.current = event.id
          applyEventToCache(qc, event)
        },
        onError: (err) => {
          if (err instanceof FatalAuthError) {
            setState('DISABLED')
            return
          }
          reconnectCountRef.current += 1
          if (reconnectCountRef.current >= MAX_RECONNECTS_BEFORE_FALLBACK) {
            setState('POLLING_FALLBACK')
            // Se corta este intento (si no, fetchEventSource seguiría reintentando por su
            // cuenta en paralelo al reintento programado) y se vuelve a probar en 5 min.
            abort.abort()
            scheduleFallbackRetry()
          } else {
            setState('RECONNECTING')
          }
        },
      })
    }

    const scheduleFallbackRetry = () => {
      if (fallbackRetryTimerRef.current) return
      fallbackRetryTimerRef.current = setTimeout(() => {
        fallbackRetryTimerRef.current = null
        reconnectCountRef.current = 0
        connect()
      }, FALLBACK_RETRY_INTERVAL_MS)
    }

    connect()

    return () => {
      cancelled = true
      abortRef.current?.abort()
      if (fallbackRetryTimerRef.current) clearTimeout(fallbackRetryTimerRef.current)
      // A null: si no, `scheduleFallbackRetry` vería un temporizador «pendiente» que ya no
      // existe y no volvería a programar el reintento (sondeo para siempre).
      fallbackRetryTimerRef.current = null
    }
  }, [opts.jwt, opts.apiBase, qc])

  return state
}

function applyEventToCache(qc: ReturnType<typeof useQueryClient>, event: ChatStreamEvent) {
  if (event.type === 'NEW_GROUP_MESSAGE') {
    // Chat de un grupo de ganas de jugar (spec 2026-10-12): la caché va por la clave del
    // grupo (el evento es igual para todos los miembros; cada uno entra con sus ganas).
    appendIfCached(qc, chatKeys.groupMessages(groupChatKey(event.group)), event.groupMessage)
    // Badges de no leídos: la bandeja de grupos y la tarjeta del grupo en el listado.
    void qc.invalidateQueries({ queryKey: playIntentKeys.chats() })
    void qc.invalidateQueries({ queryKey: playIntentKeys.allGroups() })
    return
  }
  if (event.type !== 'NEW_MESSAGE') return

  // 1) Append idempotente a chatKeys.messages(sessionId), solo si cache existe
  appendIfCached(qc, chatKeys.messages(event.sessionId), event.message)

  // 2) Invalidar la lista de chats para refrescar badge unread
  void qc.invalidateQueries({ queryKey: myChatsKeys.list })
}

/** Añade el mensaje a un chat ya en caché, sin duplicar (el optimista o el SSE pueden llegar antes). */
function appendIfCached(
  qc: ReturnType<typeof useQueryClient>,
  key: QueryKey,
  message: ChatMessage,
) {
  const current = qc.getQueryData<ChatMessage[]>(key)
  if (current && !current.some((m) => m.id === message.id)) {
    qc.setQueryData<ChatMessage[]>(key, [...current, message])
  }
}
